'use client'

import { useEffect, useState, useCallback } from 'react'
import Sentry from '@/lib/sentry-safe'
import { subscribeDownloadCount, unsubscribe } from '@/lib/realtime'

interface UseRealtimeDownloadsOptions {
  enabled?: boolean
  animationDuration?: number
}

interface DownloadUpdate {
  count: number
  increment: number
  showAnimation: boolean
}

/**
 * Hook for real-time download count with animation support
 * Shows "+1" animation when downloads increase
 */
export function useRealtimeDownloads(
  soundId: string,
  initialDownloads: number,
  options: UseRealtimeDownloadsOptions = {}
) {
  const { enabled = true, animationDuration = 2000 } = options

  const [downloadData, setDownloadData] = useState<DownloadUpdate>({
    count: initialDownloads,
    increment: 0,
    showAnimation: false
  })

  const [channelId, setChannelId] = useState<string | null>(null)

  // Reset animation after duration
  useEffect(() => {
    if (downloadData.showAnimation) {
      const timer = setTimeout(() => {
        setDownloadData((prev) => ({
          ...prev,
          showAnimation: false,
          increment: 0
        }))
      }, animationDuration)

      return () => clearTimeout(timer)
    }
  }, [downloadData.showAnimation, animationDuration])

  useEffect(() => {
    if (!enabled || !soundId) {
      return
    }

    // Subscribe to download count changes
    const id = subscribeDownloadCount(soundId, (update) => {
      const increment = update.downloads - update.previous_downloads

      setDownloadData({
        count: update.downloads,
        increment,
        showAnimation: increment > 0
      })
    })

    // eslint-disable-next-line react-hooks/set-state-in-effect -- Setting subscription ID after async subscribe is valid
    setChannelId(id)

    // Cleanup subscription on unmount
    return () => {
      if (id) {
        unsubscribe(id).catch((error) => {
          Sentry.captureException(error, { tags: { context: 'useRealtimeDownloads', action: 'unsubscribe' } })
        })
      }
    }
  }, [enabled, soundId])

  // Manual trigger for local download (when user downloads)
  const triggerLocalDownload = useCallback(() => {
    setDownloadData((prev) => ({
      count: prev.count + 1,
      increment: 1,
      showAnimation: true
    }))
  }, [])

  return {
    downloads: downloadData.count,
    increment: downloadData.increment,
    showAnimation: downloadData.showAnimation,
    isSubscribed: channelId !== null,
    triggerLocalDownload
  }
}
